/* ==========================================================
   HSM Custom Lab — Lane finder (planner)
   <cl-lane-finder> takes the items a customer needs, how many
   people, and their need-by date, then gives every item a verdict:
   possible / possible with a tradeoff / not by their date / ask the Lab.

   Lead times, minimums, lane names, button labels and links all come
   from the section settings in the theme editor. The rules that decide
   each verdict live in planFor() below.
   ========================================================== */
(function () {
  if (!window.customElements || customElements.get('cl-lane-finder')) return;

  var DAY = 86400000;

  var DEFAULTS = {
    lanes: {
      quick: { name: 'Quick Turn', cta: 'Shop Quick Turn', url: '#lane-quick' },
      team: { name: 'Team Kit', cta: 'Start your Team Kit design', url: '#lane-team' },
      build: { name: 'Full Build', cta: 'Start a project brief', url: '#lane-build' }
    },
    lab: { cta: 'Talk to the Lab', url: '#visit' },
    lead: { namebars: 3, quick: 7, sublimated: 42, twill: 70, merch: 42, custom_sticks: 120, equipment: 120 },
    min: { team: 10, merch_some: 25, custom_sticks_full: 100, custom_sticks_small: 6 }
  };

  function num(v, d) { var n = parseInt(v, 10); return isNaN(n) ? d : n; }
  function str(v, d) { return (typeof v === 'string' && v.trim() !== '') ? v : d; }

  function readConfig(el) {
    var raw = {};
    var json = el.querySelector('[data-cl-config]');
    if (json) { try { raw = JSON.parse(json.textContent); } catch (e) { raw = {}; } }
    var c = { lanes: {}, lab: {}, lead: {}, min: {} };
    ['quick', 'team', 'build'].forEach(function (k) {
      var r = (raw.lanes && raw.lanes[k]) || {};
      c.lanes[k] = { name: str(r.name, DEFAULTS.lanes[k].name), cta: str(r.cta, DEFAULTS.lanes[k].cta), url: str(r.url, DEFAULTS.lanes[k].url) };
    });
    c.lab = { cta: str(raw.lab && raw.lab.cta, DEFAULTS.lab.cta), url: str(raw.lab && raw.lab.url, DEFAULTS.lab.url) };
    Object.keys(DEFAULTS.lead).forEach(function (k) { c.lead[k] = num(raw.lead && raw.lead[k], DEFAULTS.lead[k]); });
    Object.keys(DEFAULTS.min).forEach(function (k) { c.min[k] = num(raw.min && raw.min[k], DEFAULTS.min[k]); });
    return c;
  }

  function startOfDay(d) { var x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
  function addDays(d, n) { var x = new Date(d); x.setDate(x.getDate() + n); return x; }
  function iso(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  function fmt(d) { return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }); }
  function leadLabel(days) {
    if (days == null) return 'lead time varies';
    if (days <= 3) return 'ships in 1–3 days';
    if (days <= 7) return 'ships within 1 week';
    if (days < 100) return 'about ' + Math.round(days / 7) + ' weeks';
    return 'about ' + Math.round(days / 30) + ' months';
  }
  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  class ClLaneFinder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.cfg = readConfig(this);

      this.form = this.querySelector('[data-cl-form]');
      this.dateInput = this.querySelector('[data-cl-date]');
      this.progress = this.querySelector('[data-cl-progress]');
      this.empty = this.querySelector('[data-cl-empty]');
      this.filled = this.querySelector('[data-cl-filled]');
      this.planEl = this.querySelector('[data-cl-plan]');
      this.actionsEl = this.querySelector('[data-cl-actions]');
      this.rowTpl = this.querySelector('[data-cl-row-template]');
      this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-cl-days]'));

      var self = this;
      var today = startOfDay(new Date());
      if (this.dateInput) this.dateInput.min = iso(today);

      this.chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          self.dateInput.value = iso(addDays(startOfDay(new Date()), num(chip.getAttribute('data-cl-days'), 30)));
          self.setChip(chip);
          self.update(true);
        });
      });
      this.dateInput.addEventListener('input', function () { self.setChip(null); self.update(true); });
      this.form.addEventListener('change', function (e) { if (e.target !== self.dateInput) self.update(true); });
      this.form.addEventListener('reset', function () {
        setTimeout(function () { self.setChip(null); self.update(false); }, 0);
      });
      this.form.addEventListener('submit', function (e) { e.preventDefault(); });
      this.update(false);
    }

    setChip(active) {
      this.chips.forEach(function (c) { c.setAttribute('aria-pressed', c === active ? 'true' : 'false'); });
    }

    answers() {
      var prods = Array.prototype.slice.call(this.form.querySelectorAll('input[name="prod"]:checked')).map(function (i) {
        return { id: i.value, name: i.getAttribute('data-name') || i.value };
      });
      var qtyEl = this.form.querySelector('input[name="qty"]:checked');
      return {
        prods: prods,
        qty: qtyEl ? num(qtyEl.value, 1) : null,
        qtyLabel: qtyEl ? qtyEl.getAttribute('data-label') : '',
        date: this.dateInput && this.dateInput.value ? this.dateInput.value : ''
      };
    }

    /* ---------- Verdict logic ---------- */

    evaluate(name, ideal, fallback, tradeReason, extra) {
      var days = this.days, dl = this.deadline, today = this.today;
      var orderBy = function (lead) {
        var d = addDays(dl, -lead);
        return d <= today ? 'Order today' : 'Order by ' + fmt(d);
      };
      var out = { name: name, status: '', lane: '', laneLine: '', dateLine: '', detail: '', altHead: '', altText: '', altLane: '' };

      if (ideal && days >= ideal.lead) {
        out.status = 'yes'; out.lane = ideal.lane;
        out.laneLine = this.cfg.lanes[ideal.lane].name + ' · ' + leadLabel(ideal.lead);
        out.dateLine = orderBy(ideal.lead);
        out.detail = ideal.detail + (extra ? ' ' + extra : '');
        return out;
      }
      if (ideal) {
        out.status = 'no'; out.lane = ideal.lane;
        out.laneLine = this.cfg.lanes[ideal.lane].name + ' · ' + leadLabel(ideal.lead);
        out.dateLine = 'Earliest: ' + fmt(addDays(today, ideal.lead));
        out.detail = ideal.label + ' take ' + leadLabel(ideal.lead) + ', so they won’t make your date.';
        if (fallback && (fallback.lead == null || days >= fallback.lead)) {
          out.altHead = fallback.lead == null ? 'Faster option:' : 'By ' + fmt(dl) + ':';
          out.altText = fallback.alt;
          out.altLane = fallback.lane;
        } else {
          out.altHead = 'Tight timeline:';
          out.altText = 'talk to the Lab and we’ll see what we can pull off.';
        }
        return out;
      }
      // No custom option at this quantity: the stocked option is the answer
      if (days >= fallback.lead) {
        out.status = tradeReason ? 'trade' : 'yes'; out.lane = fallback.lane;
        out.laneLine = this.cfg.lanes[fallback.lane].name + ' · ' + leadLabel(fallback.lead);
        out.dateLine = orderBy(fallback.lead);
        out.detail = tradeReason ? tradeReason + ' ' + cap(fallback.alt) : fallback.detail;
        return out;
      }
      out.status = 'no'; out.lane = fallback.lane;
      out.laneLine = this.cfg.lanes[fallback.lane].name + ' · ' + leadLabel(fallback.lead);
      out.dateLine = 'Earliest: ' + fmt(addDays(today, fallback.lead));
      out.detail = fallback.detail;
      out.altHead = 'Tight timeline:';
      out.altText = 'call or visit the Lab in Vaughan and we’ll see what we can do.';
      return out;
    }

    planFor(p, q) {
      var L = this.cfg.lead, M = this.cfg.min;
      var namebars = { lane: 'quick', lead: L.namebars, detail: 'Names, numbers or your team logo printed on Pro Blackout sticks.', alt: 'namebars + team logos on Pro Blackout sticks, ' + leadLabel(L.namebars) + '.' };
      var practice = { lane: 'quick', lead: L.quick, detail: 'Names and numbers on stocked practice jerseys.', alt: 'stocked practice jerseys with player names and numbers, ' + leadLabel(L.quick) + '.' };
      var stockGear = { lane: 'quick', lead: L.quick, detail: 'Names and numbers pressed onto in-stock tracksuits, bags and base layers.', alt: 'in-stock HSM tracksuits with names and numbers, ' + leadLabel(L.quick) + '.' };

      switch (p.id) {
        case 'stick':
          return this.evaluate(p.name, null, namebars);

        case 'stickcustom': {
          var minNote = q >= M.custom_sticks_full ? ''
            : q >= 10 ? 'Under ' + M.custom_sticks_full + ' sticks, we build it on Sherwood pro stock (' + M.custom_sticks_small + '-stick minimum).'
            : 'Full custom runs start at ' + M.custom_sticks_small + ' sticks.';
          return this.evaluate(p.name, { lane: 'build', lead: L.custom_sticks, label: 'Full custom sticks', detail: 'Your own stick graphic, designed with our team: your colours, your logo, the whole shaft.' }, namebars, null, minNote);
        }

        case 'jersey':
          if (q >= M.team) {
            var extra = this.days >= L.twill ? 'Sublimated or tackle twill both work.' : 'Sublimated only. Tackle twill needs ' + leadLabel(L.twill) + '.';
            return this.evaluate(p.name, { lane: 'team', lead: L.sublimated, label: 'Full custom uniforms', detail: 'Design your uniforms online and approve a physical sample before production.' }, practice, null, extra);
          }
          return this.evaluate(p.name, null, practice, 'Full custom uniforms start at ' + M.team + ' pieces.');

        case 'apparel':
          if (q >= M.team) {
            var ex2 = q < M.merch_some ? 'Some merch items need ' + M.merch_some + ' pieces.' : '';
            return this.evaluate(p.name, { lane: 'team', lead: L.merch, label: 'Custom team hoodies and merch', detail: 'Hoodies, tees and toques designed in your colours.' }, stockGear, null, ex2);
          }
          return this.evaluate(p.name, null, stockGear, 'Custom team merch starts at ' + M.team + ' pieces.');

        case 'quick':
          return this.evaluate(p.name, null, stockGear);

        case 'gloves':
          return this.evaluate(p.name,
            { lane: 'build', lead: L.equipment, label: 'Custom-colourway gloves and pants', detail: 'Built with our factory partners, with a Custom Lab rep guiding the spec.' },
            { lane: 'build', lead: null, alt: 'team-colourway special orders on standard models usually arrive sooner. Ask the Lab.' });

        case 'gifts':
          return {
            name: p.name, status: 'ask', lane: 'build',
            laneLine: this.cfg.lanes.build.name + ' · lead time varies',
            dateLine: 'Tell us your date',
            detail: 'Commemorative sticks, made-of-sticks gifts and engraved accessories. Timing depends on the item.',
            altHead: this.days < 14 ? 'Short on time?' : '',
            altText: this.days < 14 ? 'Some in-stock gifts can be engraved quickly.' : '',
            altLane: ''
          };
      }
      return null;
    }

    /* ---------- Rendering ---------- */

    statusLabel(st, dl) {
      if (st === 'yes') return 'Possible';
      if (st === 'trade') return 'Possible, with a tradeoff';
      if (st === 'no') return 'Not by ' + fmt(dl);
      return 'Ask the Lab';
    }

    renderRow(it) {
      var node = this.rowTpl.content.firstElementChild.cloneNode(true);
      node.setAttribute('data-status', it.status);
      var set = function (slot, val) { var el = node.querySelector('[data-slot="' + slot + '"]'); if (el) el.textContent = val; };
      set('name', it.name);
      set('status', this.statusLabel(it.status, this.deadline));
      set('lane', it.laneLine);
      set('date', it.dateLine);
      set('detail', it.detail);
      var alt = node.querySelector('[data-cl-alt]');
      if (it.altText) {
        set('alt-head', it.altHead);
        set('alt-text', it.altText);
        alt.hidden = false;
      } else {
        alt.hidden = true;
      }
      return node;
    }

    renderActions(lanes) {
      var self = this;
      this.actionsEl.textContent = '';
      var list = [];
      ['quick', 'team', 'build'].forEach(function (k) {
        if (lanes[k]) list.push({ label: self.cfg.lanes[k].cta, url: self.cfg.lanes[k].url });
      });
      list.push({ label: this.cfg.lab.cta, url: this.cfg.lab.url });
      var arrow = this.querySelector('[data-cl-arrow-template]');
      list.forEach(function (a, i) {
        var link = document.createElement('a');
        link.className = 'cl-btn ' + (i === 0 ? 'cl-btn--light' : 'cl-btn--ghost');
        link.href = a.url;
        var span = document.createElement('span');
        span.textContent = a.label;
        link.appendChild(span);
        if (arrow) link.appendChild(arrow.content.firstElementChild.cloneNode(true));
        self.actionsEl.appendChild(link);
      });
    }

    update(fromUser) {
      var a = this.answers();
      var count = (a.prods.length ? 1 : 0) + (a.qty ? 1 : 0) + (a.date ? 1 : 0);
      if (this.progress) this.progress.textContent = count + ' of 3 answered';
      if (this.dateInput) this.dateInput.classList.toggle('is-set', !!a.date);

      if (count < 3) {
        this.empty.hidden = false;
        this.filled.hidden = true;
        return;
      }

      var self = this;
      this.today = startOfDay(new Date());
      this.deadline = startOfDay(new Date(a.date + 'T00:00:00'));
      this.days = Math.round((this.deadline - this.today) / DAY);

      var plan = a.prods.map(function (p) { return self.planFor(p, a.qty); }).filter(Boolean);
      var lanes = {};
      plan.forEach(function (it) {
        if (it.status !== 'no' && it.lane) lanes[it.lane] = true;
        if (it.altLane) lanes[it.altLane] = true;
      });

      var ok = plan.filter(function (x) { return x.status === 'yes' || x.status === 'trade'; }).length;
      this.querySelector('[data-slot="deadline"]').textContent = fmt(this.deadline);
      this.querySelector('[data-slot="qty"]').textContent = a.qtyLabel + ' people';
      this.querySelector('[data-slot="summary"]').textContent =
        ok + ' of ' + plan.length + ' item' + (plan.length === 1 ? '' : 's') + ' can be ready by your date' +
        (this.days >= 0 ? ' (' + this.days + ' day' + (this.days === 1 ? '' : 's') + ' away).' : '.');

      this.planEl.textContent = '';
      plan.forEach(function (it) { self.planEl.appendChild(self.renderRow(it)); });
      this.renderActions(lanes);

      this.empty.hidden = true;
      this.filled.hidden = false;

      if (fromUser) {
        var detail = {
          deadline: a.date, quantity: a.qtyLabel,
          items: plan.map(function (it) { return { item: it.name, verdict: it.status, lane: it.lane }; })
        };
        this.dispatchEvent(new CustomEvent('custom-lab:plan', { bubbles: true, detail: detail }));
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push({
            event: 'custom_lab_plan',
            custom_lab_items: plan.map(function (it) { return it.name + ':' + it.status; }).join('|'),
            custom_lab_quantity: a.qtyLabel,
            custom_lab_days_out: this.days
          });
        }
      }
    }
  }

  customElements.define('cl-lane-finder', ClLaneFinder);
})();
